import type { BondSchedule } from "./useBondSchedule";

export type LifelineEvent = {
  date: Date;
  iso: string;
  kind: "coupon" | "amort";
  amount: number | null;
};

export type LifelineSummary = {
  pastTotal: number;
  pastCount: number;
  pastAvg: number;
  pastMin: number | null;
  pastMax: number | null;
  futureCount: number;
  futureCoupons: number; // суммарный объявленный купон в будущем
  faceRedemption: number; // сумма всех будущих amortizations (включая погашение)
  maturityDate: Date | null;
  maturityIso: string | null;
};

// Парсим расписание один раз, отдаём отсортированные события + сводку для
// карточек под лентой. Чистая функция без React — тестируется и переиспользуется.
export function buildLifeline(schedule: BondSchedule): {
  events: LifelineEvent[];
  past: LifelineEvent[];
  future: LifelineEvent[];
  summary: LifelineSummary;
} {
  const today = new Date();
  const events: LifelineEvent[] = [];

  for (const c of schedule.coupons) {
    events.push({
      date: parseIsoDate(c.date),
      iso: c.date,
      kind: "coupon",
      amount: c.amount,
    });
  }
  for (const a of schedule.amortizations) {
    events.push({
      date: parseIsoDate(a.date),
      iso: a.date,
      kind: "amort",
      amount: a.amount,
    });
  }
  events.sort((x, y) => +x.date - +y.date);

  const past = events.filter((e) => e.date <= today);
  const future = events.filter((e) => e.date > today);

  const pastCoupons = past.filter(
    (e) => e.kind === "coupon" && e.amount != null,
  );
  const pastAmounts = pastCoupons.map((e) => e.amount as number);
  const pastTotal = pastAmounts.reduce((s, v) => s + v, 0);
  const pastCount = pastCoupons.length;
  const pastAvg = pastCount > 0 ? pastTotal / pastCount : 0;
  const pastMin = pastCount > 0 ? Math.min(...pastAmounts) : null;
  const pastMax = pastCount > 0 ? Math.max(...pastAmounts) : null;

  const futureCoupons = future
    .filter((e) => e.kind === "coupon" && e.amount != null)
    .reduce((s, e) => s + (e.amount ?? 0), 0);
  const faceRedemption = future
    .filter((e) => e.kind === "amort")
    .reduce((s, e) => s + (e.amount ?? 0), 0);

  const lastEvent = events[events.length - 1] ?? null;

  return {
    events,
    past,
    future,
    summary: {
      pastTotal,
      pastCount,
      pastAvg,
      pastMin,
      pastMax,
      futureCount: future.length,
      futureCoupons,
      faceRedemption,
      maturityDate: lastEvent ? lastEvent.date : null,
      maturityIso: lastEvent ? lastEvent.iso : null,
    },
  };
}

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}
